import React from "react";
import { assetUrl } from "../lib/api";
import { usePlayerCrop } from "./PlayerCropContext";

const SIZE = {
    xs: "h-5 w-5 text-[9px]",
    sm: "h-7 w-7 text-[10px]",
    md: "h-9 w-9 text-xs",
    lg: "h-12 w-12 text-sm",
    xl: "h-16 w-16 text-base",
};

/**
 * Compact circular headshot with letter fallback.
 *
 *  - When `playerId` is provided AND `showTierRing` is true, the avatar is
 *    rendered with a subtle 2px ring matching the viewer's Favourite tier
 *    (or the admin's public picks for anonymous visitors).
 *  - `showTierRing` defaults to TRUE so player-focused tables (Leaderboards,
 *    Lineups) get the ring automatically. Pass `showTierRing={false}` for
 *    contexts where the user explicitly wants the visual to stay neutral
 *    (e.g. Home page Favourites cards).
 */
export const PlayerAvatar = ({ photo, name = "", size = "sm", playerId = "", showTierRing = true, photoX, photoY, photoZoom, sizePx, square = false }) => {
    const sq = (c) => (square ? c.replace(/rounded-full/g, "rounded-md") : c);
    const cls = sizePx ? "rounded-full overflow-hidden shrink-0 inline-flex items-center justify-center aspect-square" : (SIZE[size] || SIZE.sm);
    const pxStyle = sizePx ? { width: sizePx, height: sizePx, fontSize: Math.round(sizePx * 0.4) } : null;
    // Global crop lookup by playerId — keeps framing identical everywhere
    // (matching Player Admin) even when a call site doesn't pass the offsets.
    const crop = usePlayerCrop(playerId);
    // NHL headshots (numeric IDs) get a default face zoom site-wide so faces read
    // larger; any explicit prop or saved crop still wins.
    const isNhl = /^\d{4,}$/.test(String(playerId || ""));
    // Explicit prop wins; else the looked-up crop; else NHL face-zoom / neutral defaults.
    const px = photoX ?? crop?.x ?? 50;
    const py = photoY ?? crop?.y ?? (isNhl ? 30 : 50);
    const pz = photoZoom ?? crop?.z ?? (isNhl ? 1.55 : 1);
    // Favourite-tier rings removed sitewide (no coloured circles on headshots).
    const ringStyle = null;

    if (photo) {
        // Non-destructive crop: object-position frames the photo (e.g. top for
        // portrait NCAA headshots), transform scale zooms, both anchored to the
        // same focal point so it stays intuitive.
        const cropStyle = {
            objectPosition: `${px}% ${py}%`,
            transform: pz && pz !== 1 ? `scale(${pz})` : undefined,
            transformOrigin: `${px}% ${py}%`,
        };
        return (
            <span className={sq(`${cls} bg-black/30 ${sizePx ? "border border-white/10" : "rounded-full overflow-hidden border border-white/10 shrink-0 inline-flex items-center justify-center aspect-square"}`)} style={{ ...(ringStyle || {}), ...(pxStyle || {}) }} data-testid="player-avatar-wrap">
                <img
                    src={assetUrl(photo)}
                    alt={name}
                    className="block w-full h-full object-cover"
                    style={cropStyle}
                    data-testid="player-avatar"
                />
            </span>
        );
    }
    const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <span
            className={sq(`${cls} rounded-full bg-[#1E2233] border border-white/10 inline-flex items-center justify-center font-mono-stat font-bold text-white/70 shrink-0`)}
            style={{ ...(ringStyle || {}), ...(pxStyle || {}) }}
            data-testid="player-avatar-fallback"
        >
            {initials || "?"}
        </span>
    );
};
